import { zodResolver } from '@hookform/resolvers/zod'
import SaveIcon from '@mui/icons-material/Save'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material'
import { FormInput } from 'components/FormItem/form-input'
import { useFetchData } from 'hooks/use-fetch-data'
import { useMutateData } from 'hooks/use-mutate-data'
import { useCallback, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { apiV1 } from 'services'
import { toast } from 'sonner'
import { IBrasilCidade } from 'types/BrasilCidade'
import { ILocationProps } from 'types/locationProps'
import { functionWithLoading, validation } from 'utils'
import { z } from 'zod'

const completeRegistrationSchema = z.object({
  id: z.number(),
  nome: z
    .string()
    .min(1, { message: 'É necessário preencher o sobrenome' })
    .nullable(),
  cpf: z
    .string()
    .min(1, { message: 'É necessário preencher o CPF' })
    .refine((value) => validation.isValidCPF(value ?? ''), {
      message: 'CPF Inválido',
    })
    .nullable(),
  data_nascimento: z
    .string()
    .nonempty({ message: 'Informe a data de nascimento' })
    .nullable(),
  telefone: z
    .string()
    .min(11, { message: 'O número deve conter 11 dígitos' })
    .max(15, { message: 'O número deve conter 11 dígitos' })
    .nonempty({ message: 'É necessário preencher o telefone' })
    .nullable(),
  sexo: z
    .string()
    .nonempty({ message: 'Por favor selecione o sexo' })
    .refine((val) => val !== '0', { message: 'Por favor selecione o sexo' })
    .nullable(),
  cod_brasil_cidade: z
    .number()
    .refine((val) => val !== 0, { message: 'É necessário selecionar a cidade' })
    .nullable(),
})

type CompleteRegistrationFormSchema = z.infer<typeof completeRegistrationSchema>

const CompleteRegistrationPage = () => {
  const [selectedUF, setSelectedUF] = useState<string>('SP')
  const [ufs, setUfs] = useState<IBrasilCidade[]>([])
  const [cities, setCities] = useState<IBrasilCidade[]>([])
  const [loadingUf, setLoadingUf] = useState(true)
  const [loadingCity, setLoadingCity] = useState(true)
  const [showCPF, setShowCPF] = useState(true)
  const [showPhone, setShowPhone] = useState(true)

  const navigate = useNavigate()
  const location = useLocation() as ILocationProps
  const from = location.state?.from?.pathname ?? '/'
  const fromState = location.state?.from?.state

  const form = useForm<CompleteRegistrationFormSchema>({
    resolver: zodResolver(completeRegistrationSchema),
  })

  const getUser = useFetchData(
    async () => await apiV1.userService.getUserByToken(),
  )

  const verifyIfAlreadyExists = useMutateData(
    apiV1.userService.existInDatabase,
    {
      onSuccess: async ({ data }) => {
        if (data.cpf) toast.error('CPF já cadastrado')
        if (data.email) toast.error('E-mail já cadastrado')
        if (data.phone) toast.error('Telefone já cadastrado')
        if (data.email || data.cpf || data.phone) return
        const values = form.getValues()
        await saveUser.mutateAsync(values)
      },
      onError: (error) => {
        toast.error('Erro ao verificar dados: ' + error.message)
      },
    },
  )

  const saveUser = useMutateData(apiV1.userService.completeRegistration, {
    onSuccess: (data) => {
      toast.success('Usuário salvo com sucesso')
      localStorage.setItem('user', JSON.stringify(data))
      navigate(from, { replace: true, state: fromState })
    },
    onError: (error) => {
      toast.error('Erro ao salvar usuário: ' + error.message)
    },
  })

  useEffect(() => {
    const user = getUser.data?.data
    if (user) {
      localStorage.setItem('user', JSON.stringify(user))
      if (user.cpf && user.ddd && user.telefone) {
        navigate(from, { replace: true, state: fromState })
      }
      form.reset({
        id: user.id,
        nome: [user.nome, user.sobrenome].join(' ').trim() || '',
        cpf: user.cpf ?? '',
        data_nascimento: user.data_nascimento
          ? typeof user.data_nascimento === 'string'
            ? user.data_nascimento
            : new Date(user.data_nascimento).toISOString().slice(0, 10)
          : '',
        telefone: [user.ddd, user.telefone].join('') || '',
        sexo: user.sexo ?? '',
        cod_brasil_cidade: user.brasil_cidade?.codigo_cidade ?? 0,
      })
      if (user.cpf) setShowCPF(false)
      if (user.telefone) setShowPhone(false)
      setSelectedUF(user.brasil_cidade?.uf ?? 'SP')
      functionWithLoading(
        apiV1.cityService
          .getUFs()
          .then((resUF) => {
            setUfs(resUF.data)
            functionWithLoading(
              apiV1.cityService
                .getCities(user.brasil_cidade?.uf ?? 'SP')
                .then((res) => {
                  setCities(res.data)
                }),
              setLoadingCity,
            )
          })
          .catch((error) => console.log(error)),
        setLoadingUf,
      )
    }
  }, [form, from, fromState, getUser.data?.data, location.state.from, navigate])

  useEffect(() => {
    apiV1.cityService
      .getUFs()
      .then((resUF) => {
        setUfs(resUF.data)
        functionWithLoading(
          apiV1.cityService.getCities(selectedUF).then((res) => {
            setCities(res.data)
          }),
          setLoadingCity,
        )
      })
      .catch((error) => console.log(error))
  }, [selectedUF])

  const handleChangeUF = useCallback((event) => {
    if (event) {
      setSelectedUF(event.target.value)
      functionWithLoading(
        apiV1.cityService.getCities(event.target.value).then((res) => {
          setCities(res.data)
        }),
        setLoadingCity,
      )
    }
  }, [])

  console.log(form.formState.errors)

  const onSubmit = form.handleSubmit(
    useCallback(
      async (data: CompleteRegistrationFormSchema) => {
        await verifyIfAlreadyExists.mutateAsync({
          cpf: data.cpf ?? '',
          phone: data.telefone ?? '',
        })
      },
      [verifyIfAlreadyExists],
    ),
  )

  return (
    <Container
      component="form"
      onSubmit={onSubmit}
      noValidate
      maxWidth="md"
      sx={{ my: 2 }}
    >
      <Paper sx={{ p: 2 }}>
        <Typography variant="h4">Complete seu cadastro</Typography>

        {getUser.isLoading ? (
          <Grid container spacing={2} my={2}>
            <Grid item xs={12} md={6}>
              <Skeleton />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton />
            </Grid>
            <Grid item xs={12}>
              <Skeleton />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton />
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2} my={2}>
            {showCPF && (
              <Grid item xs={12} sm={6}>
                <FormInput
                  control={form.control}
                  name="cpf"
                  label="CPF"
                  mask="999.999.999-99"
                />
              </Grid>
            )}
            {showPhone && (
              <Grid item xs={12} sm={6}>
                <FormInput
                  control={form.control}
                  name="telefone"
                  label="Telefone"
                  mask="(99) 99999-9999"
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <FormInput
                control={form.control}
                name="nome"
                label="Nome completo"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormInput
                control={form.control}
                name="data_nascimento"
                label="Data de nascimento"
                type="date"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel id="sexo-select-label">Sexo</InputLabel>
                <Controller
                  name="sexo"
                  control={form.control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="sexo-select-label"
                      id="sexo-select"
                      label="Sexo"
                      defaultValue="0"
                      error={!!form.formState.errors.sexo}
                    >
                      <MenuItem value={'Masculino'}>Masculino</MenuItem>
                      <MenuItem value={'Feminino'}>Feminino</MenuItem>
                      <MenuItem value={'Prefiro não informar'}>
                        Prefiro não informar
                      </MenuItem>
                      <MenuItem value={'0'}>Selecione</MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={2}>
              {loadingUf ? (
                <Skeleton />
              ) : (
                <FormControl fullWidth>
                  <InputLabel id="uf-select-label">UF</InputLabel>
                  <Select
                    labelId="uf-select-label"
                    id="uf-select"
                    label="UF"
                    value={selectedUF}
                    onChange={handleChangeUF}
                  >
                    {ufs?.map((city) => (
                      <MenuItem key={'uf_' + city.uf} value={city.uf}>
                        {city.uf}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} sm={10}>
              {loadingCity ? (
                <Skeleton />
              ) : (
                <Controller
                  name="cod_brasil_cidade"
                  control={form.control}
                  render={({ field }) => (
                    <Autocomplete
                      disablePortal
                      id="cod_brasil_cidade-select"
                      options={cities}
                      getOptionLabel={(option) => option.cidade}
                      onChange={(_, value) =>
                        form.setValue(
                          'cod_brasil_cidade',
                          value?.codigo_cidade ?? 0,
                        )
                      }
                      value={
                        cities.find((c) => c.codigo_cidade === field.value) ||
                        null
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Cidade"
                          name="cod_brasil_cidade"
                          error={!!form.formState.errors.cod_brasil_cidade}
                          helperText={
                            form.formState.errors.cod_brasil_cidade?.message
                          }
                        />
                      )}
                      loading={loadingCity}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
              }}
            >
              <LoadingButton
                loadingPosition="end"
                endIcon={<SaveIcon />}
                loading={saveUser.isPending}
                disabled={saveUser.isPending}
                variant="contained"
                type="submit"
              >
                Salvar alterações
              </LoadingButton>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  )
}

export default CompleteRegistrationPage
